/**
 * SignInPrompt — a friendly "sign in to continue" card for logged-out users on
 * otherwise-public storefront pages (coins / add-ons / bundles). Replaces the
 * bare error/redirect path: the page still renders, and this prompts sign-in
 * while preserving the current location so the user returns here after login.
 */

import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { LogIn } from 'lucide-react';

import Button from '../base/Button';

interface SignInPromptProps {
  /** Already-translated message, e.g. "Sign in to view your coins". */
  message: string;
  className?: string;
}

export default function SignInPrompt({ message, className = '' }: SignInPromptProps) {
  const { t } = useTranslation('common');
  const location = useLocation();

  return (
    <div
      className={`max-w-md mx-auto text-center bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/15 border border-[var(--pv-color-accent-soft)] dark:border-[#004D40]/40 rounded-2xl p-8 ${className}`}
    >
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-white/70 dark:bg-[#004D40]/20 flex items-center justify-center">
        <LogIn className="w-6 h-6 text-[#004D40]" aria-hidden="true" />
      </div>
      <p className="text-[#003930] dark:text-[#B7DDD7] font-medium mb-4">{message}</p>
      {/* Preserve the current location so sign-in returns the user here. */}
      <Link to="/signin" state={{ from: location }}>
        <Button>{t('auth.signIn')}</Button>
      </Link>
    </div>
  );
}
